/**
 * Mobile (Capacitor) platform adapter.
 *
 * Wraps Capacitor plugins for native mobile capabilities.
 * Optimised for touch interaction with reduced feature set.
 */
import { PlatformAdapter } from "./platform.interface";
import { createPortableUuid } from "./uuid";

export class MobilePlatform implements PlatformAdapter {
  readonly type = "mobile" as const;
  readonly supportsCustomTitleBar = false;
  readonly supportsAmiBroker = false;
  readonly maxChartPanels = 2;
  readonly fullDrawingTools = false; // Reduced set for touch
  readonly supportsLocalBacktest = false; // View results only
  readonly tickBatchIntervalMs = 200; // Slower for battery life

  // ─── Device Identity ─────────────────────────────────

  async getDeviceId(): Promise<string> {
    try {
      const { Device } = await import("@capacitor/device");
      const info = await Device.getId();
      return info.identifier;
    } catch {
      let id = localStorage.getItem("_mobile_device_id");
      if (!id) {
        id = createPortableUuid();
        localStorage.setItem("_mobile_device_id", id);
      }
      return id;
    }
  }

  getDeviceName(): string {
    try {
      const ua = navigator.userAgent;
      if (/android/i.test(ua)) return "Android Device";
      if (/iPad|iPhone|iPod/.test(ua)) return "iOS Device";
    } catch {
      // ignore
    }
    return "Mobile Device";
  }

  // ─── Window Management (no-ops) ─────────────────────

  async minimizeWindow(): Promise<void> {
    /* no-op */
  }
  async maximizeWindow(): Promise<void> {
    /* no-op */
  }
  async closeWindow(): Promise<void> {
    /* no-op */
  }

  // ─── Storage (Capacitor Secure Storage) ──────────────

  async getStorageItem(key: string): Promise<string | null> {
    try {
      const { Preferences } = await import("@capacitor/preferences");
      const { value } = await Preferences.get({ key });
      return value;
    } catch {
      return localStorage.getItem(key);
    }
  }

  async setStorageItem(key: string, value: string): Promise<void> {
    try {
      const { Preferences } = await import("@capacitor/preferences");
      await Preferences.set({ key, value });
    } catch {
      localStorage.setItem(key, value);
    }
  }

  async removeStorageItem(key: string): Promise<void> {
    try {
      const { Preferences } = await import("@capacitor/preferences");
      await Preferences.remove({ key });
    } catch {
      localStorage.removeItem(key);
    }
  }

  // ─── Notifications ───────────────────────────────────

  async showNotification(
    title: string,
    body: string,
    _data?: Record<string, unknown>
  ): Promise<void> {
    // Use the Web Notification API as a lightweight fallback.
    // LocalNotifications (Capacitor) is omitted to avoid pulling in
    // the push-notifications permission flow on Android.
    if (Notification.permission === "granted") {
      new Notification(title, { body });
    }
  }

  async requestNotificationPermission(): Promise<boolean> {
    // Push notifications (FCM) are not configured for this app.
    // Returning false prevents the system permission dialog from appearing
    // and avoids a fatal crash caused by PushNotifications.register()
    // attempting to contact Firebase without a google-services.json.
    return false;
  }

  async getPushToken(): Promise<string | null> {
    // FCM push notifications are not configured — no token available.
    return null;
  }

  // ─── Lifecycle Events ────────────────────────────────

  onAppClose(callback: () => void): () => void {
    // On mobile, "close" means backgrounding
    const handler = () => {
      if (document.visibilityState === "hidden") callback();
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }

  onAppResume(callback: () => void): () => void {
    const handler = () => {
      if (document.visibilityState === "visible") callback();
    };
    document.addEventListener("visibilitychange", handler);

    // Also listen for Capacitor App plugin resume
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        App.addListener("appStateChange", (state) => {
          if (state.isActive) callback();
        });
      } catch {
        // Capacitor not available
      }
    })();

    return () => document.removeEventListener("visibilitychange", handler);
  }

  // ─── OAuth / Auth ─────────────────────────────────────────────────────────

  getOAuthClientId(): string {
    // Mobile-specific client ID injected via the Vite HTML shim (REACT_APP_OAUTH_CLIENT_ID_MOBILE).
    // Set in .env (dev) or .env.production (prod). Fallback matches application[-prod].properties.
    return (
      process.env.REACT_APP_OAUTH_CLIENT_ID_MOBILE ||
      process.env.REACT_APP_OAUTH_CLIENT_ID ||
      process.env.REACT_APP_CLIENTID ||
      "marketview-mobile-client"
    );
  }

  getOAuthRedirectUri(): string {
    // Mobile uses a custom URI scheme so the OS can route the auth server's
    // redirect back to this app without relying on https://localhost/.
    // This URI must be registered in the auth server for the mobile client.
    return (
      process.env.REACT_APP_OAUTH_REDIRECT_URI_MOBILE ||
      "com.marketview://auth/callback"
    );
  }

}
